namespace Solovis.Reports.Parts.Tables {

  export interface DataTablePartSettings {
    chartOptions?: DataTablePartSettingsChartOptions;

    /**
     * Function that returns customFunctions to add for parameter transform processing for custom columns.
     */
    customFunctions?: (tableControl: DataTableControl) => {
      [key: string]: any;
    }

    /**
     * Called when an editable cell commits a new value. Mutates `row` to recompute any derived
     * cells (e.g. dependent fields, computed totals) before the table re-renders.
     */
    onRowChanged?: (table: DataTableControl, row: any, change: { col: any, oldValue: any, newValue: any }) => void;
  }

  export interface DataTablePartSettingsChartOptions {
    defaultChartState?: TableAsChartState;

    chartStateTemplates: DataTablePartSettingsChartOptionsTemplate[];

    /**
     * When true, the table is hidden on initial load so only the chart is shown. The user can still
     * reveal the grid via the chart's "Show Table" option.
     */
    hideTable?: boolean;
  }

  export interface DataTablePartSettingsChartOptionsTemplate {
    name: string;
    chartState: TableAsChartState | (() => TableAsChartState);
  }


  export class DataTableControl extends Solovis.Reports.Parts.PartControl {

    public static _log: boolean = false;

    public _persistedSettings;

    public _tableSettings: DataTablePartSettings;

    public columnSet: Solovis.Reports.Parts.Tables.DataTableHelpers.ColumnSet;
    public sortManager: Solovis.Reports.Parts.Tables.DataTableHelpers.SortManager;
    public groupManager: Solovis.Reports.Parts.Tables.DataTableHelpers.GroupManager;
    public filterManager: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterManager;
    public expansionManager: Solovis.Reports.Parts.Tables.DataTableHelpers.ExpansionManager;
    public rowDataExporter: Solovis.Reports.Parts.Tables.DataTableHelpers.RowDataExporter;

    public _aggregateRow;
    public _tableMods;
    public _filterFirst;
    public _rowFilter;
    public _modelEnvironmentSettings;
    public _hideIfNoRows;
    public _subRowFactory;
    public _subRowGroupKeyGetter;
    public _keepGroupSubRowsOnCollapse;
    public _removeClaimedNestedRows: boolean;
    public _rowStyleUpdater;

    public dataView;

    public _actionBarDiv: JQuery;
    public tableContainer: JQuery;

    public _initialPostUpdateCompleted;
    public _initialSavedChartData: any;
    public _chartContainer: JQuery;
    public _chartControlHost: Solovis.UI.ControlHost;
    public _tableAsChartControl: TableAsChartControl;

    public _tableSettingsToUse;
    public _debouncedUpdateRowFilter;
    public _latestTableRenderer;

    public modelEnvironment: InvData.ModelEnvironment;

    public _sortingAndGroupingDialog: Solovis.UI.Dialog;
    public _filteringDialog: Solovis.Reports.Parts.Tables.DataTableHelpers.FilteringDialog;

    constructor(options) {
      super(options);

      DataTableHelpers.CellEditors.registerBuiltInEditors();

      this._initialPostUpdateCompleted = false;

      this._tableSettings = this.settings;

      this.dataSets = null;

      let self = this;

      this._persistedSettings = options.persistedSettings ? options.persistedSettings : null;

      // Initialize columnSet.
      let columnGenerator = function () {
        let columnDefinitions = null;

        if (self.settings.columns) {

          if (typeof self.settings.columns == "function") {
            columnDefinitions = self.settings.columns.call(self);
          }
          else {
            columnDefinitions = self.settings.columns;
          }
        }

        // See if there are site report settings that we need to use to override the default column definitions.
        let siteReportSettings = self.reportControl.getSiteReportSettings();
        if (siteReportSettings && siteReportSettings.columns) {
          if (columnDefinitions) {
            for (let i = 0; i < columnDefinitions.length; i++) {
              let colDef = columnDefinitions[i];

              for (let colSiteSetting of siteReportSettings.columns) {
                if (colDef.id === colSiteSetting.id) {
                  let newColDef: any = {};

                  // Shallow copy settings.
                  for (let key in colDef) {
                    if (colDef.hasOwnProperty(key)) {
                      newColDef[key] = colDef[key];
                    }
                  }

                  if (Solovis.isNotNull(colSiteSetting.label)) {
                    newColDef.label = colSiteSetting.label;
                  }
                  if (Solovis.isNotNull(colSiteSetting.width)) {
                    newColDef.width = colSiteSetting.width;
                  }
                  if (Solovis.isNotNull(colSiteSetting.hiddenByDefault)) {
                    newColDef.hiddenByDefault = colSiteSetting.hiddenByDefault;
                  }
                  if (Solovis.isNotNull(colSiteSetting.superHeaders)) {
                    newColDef.superHeaders = colSiteSetting.superHeaders;
                  }

                  columnDefinitions[i] = newColDef;
                }
              }
            }
          }
        }

        return columnDefinitions;
      };

      this.columnSet = new Solovis.Reports.Parts.Tables.DataTableHelpers.ColumnSet({
        tableControl: this,
        columnGenerator: columnGenerator,
        columnVisibilityInitialFilterCreator: this.settings.columnVisibilityInitialFilterCreator ? this.settings.columnVisibilityInitialFilterCreator : null,
        columnOrderIds: (this._persistedSettings && this._persistedSettings.columnOrderIds) ? this._persistedSettings.columnOrderIds : null,
        visibleColumnIds: (this._persistedSettings && this._persistedSettings.visibleColumnIds) ? this._persistedSettings.visibleColumnIds : null,
        hiddenColumnIds: (this._persistedSettings && this._persistedSettings.hiddenColumnIds) ? this._persistedSettings.hiddenColumnIds : null,
        columnMods: (this._persistedSettings && this._persistedSettings.columnMods) ? this._persistedSettings.columnMods : null,
        customColumnSettings: (this._persistedSettings && this._persistedSettings.customColumns) ? this._persistedSettings.customColumns : null
      });

      $(this.columnSet).bind("columnsChanged", (function (e, data) { self._handleColumnsChanged(e, data); } as any))

      // Initialize sortManager.
      var sortBy = this.settings.sortBy;
      if (this._persistedSettings) {
        sortBy = this._persistedSettings.sortBy;
      }

      this.sortManager = new Solovis.Reports.Parts.Tables.DataTableHelpers.SortManager({
        tableControl: this,
        sorts: this.settings.sorts,
        isStickySortCallback: this.settings.isStickySortCallback,
        sortBy: sortBy
      });

      $(this.sortManager).bind("sortByChanged", function () { self._handleSortByChanged(); });

      // Initialize groupManager.
      var groupBy = this.settings.groupBy;
      if (this._persistedSettings) {
        groupBy = this._persistedSettings.groupBy;
      }

      this.groupManager = new Solovis.Reports.Parts.Tables.DataTableHelpers.GroupManager({
        tableControl: this,
        groups: this.settings.groups,
        aggregates: this.settings.aggregates,
        groupBy: groupBy
      });

      $(this.groupManager).bind("groupByChanged", function () { self._handleGroupByChanged(); });

      // Initialize filterManager.
      var filterBy = null;
      if (this._persistedSettings) {
        filterBy = this._persistedSettings.filterBy;
      }

      this.filterManager = new Solovis.Reports.Parts.Tables.DataTableHelpers.FilterManager({
        tableControl: this,
        filterBy: filterBy
      });

      if (this._persistedSettings) {
        this._filterFirst = !!this._persistedSettings.filterFirst;
      }

      $(this.filterManager).bind("filterByChanged", function () { self._handleFilterByChanged(); });
      $(this).bind("filterFirstChanged", function () { self._handleFilterFirstChanged(); });

      // Initialize expansionManager.
      var expansionSettings = null;
      if (this._persistedSettings) {
        expansionSettings = this._persistedSettings.expansionSettings;
      }
      else if (options.settings && options.settings.expansionSettings) {
        expansionSettings = options.settings.expansionSettings;
      }

      this.expansionManager = new Solovis.Reports.Parts.Tables.DataTableHelpers.ExpansionManager({
        tableControl: this,
        expansionSettings: expansionSettings
      });

      $(this.expansionManager).bind("expansionChanged", function () { self._handleExpansionChanged(); });


      if (this.settings.aggregateRow) {
        this._aggregateRow = this.settings.aggregateRow;
      }
      else {
        this._aggregateRow = {};
      }

      // See if there's a chart we have to initialize after first load.
      if (this._persistedSettings && this._persistedSettings.chart) {
        this._initialSavedChartData = this._persistedSettings.chart;
      }
      else if (this._tableSettings.chartOptions) {
        // Populate initial chart state with the default chart state.
        if (this._tableSettings.chartOptions.defaultChartState) {
          let defaultChartState: any = this._tableSettings.chartOptions.defaultChartState;
          this._initialSavedChartData = {
            chartState: (typeof defaultChartState === "function") ? defaultChartState() : defaultChartState,
            hideTable: this._tableSettings.chartOptions.hideTable
          };
        }
      }

      // Initialize the tableMods.
      this._tableMods = null;
      if (this._persistedSettings && this._persistedSettings.tableMods) {
        this._tableMods = this._persistedSettings.tableMods;
      }
      this._updateTableSettingsToUse();

      // rowFilter should be a function that takes a row object and returns whether or not it should be visible.
      this._rowFilter = null;

      this._modelEnvironmentSettings = this.settings.modelEnvironment ? this.settings.modelEnvironment : null;
      this._hideIfNoRows = this.settings.hideIfNoRows ? this.settings.hideIfNoRows : false;

      this._subRowFactory = this.settings.subRowFactory ? this.settings.subRowFactory : null;
      this._subRowGroupKeyGetter = this.settings.subRowGroupKeyGetter ? this.settings.subRowGroupKeyGetter : null;
      this._keepGroupSubRowsOnCollapse = !!this.settings.keepGroupSubRowsOnCollapse;
      this._removeClaimedNestedRows = !!this.settings.removeClaimedNestedRows;

      this._rowStyleUpdater = this.settings.rowStyleUpdater ? this.settings.rowStyleUpdater : null;

      this.dataView = null;

      // Setup the rowDataExporter.
      this.rowDataExporter = new Solovis.Reports.Parts.Tables.DataTableHelpers.RowDataExporter({
        tableControl: this,
        persistedSettings: this._persistedSettings ? this._persistedSettings.exporter : null
      });
    }


    public doInit() {
    }

    public doRender() {
      var self = this;

      if (DataTableControl._log) {
        console.log("DataTableControl.doRender");
      }

      this.container.html("");

      this._actionBarDiv = $("<div>").addClass("no-print").css("display", "none").appendTo(this.container);

      this.tableContainer = $("<div>").appendTo(this.container);

      if (this._initialSavedChartData && this._initialSavedChartData.hideTable) {
        this.tableContainer.toggle(false);
      }
      if (this._initialSavedChartData && this._initialSavedChartData.hideChart) {
        this._hideChart();
      }

      if (this.dataView) {
        this._renderTableData();
      }

      // Update the action bar whenever filters are changed.
      $(this.filterManager).bind("filterByChanged", function () {
        self._updateActionBarDiv();
      });

      self._updateActionBarDiv();

      if (Solovis.UX.Utils.areNewControlsSupported()) {
        Solovis.UX.Utils.subscribe("@solovis/report/show-chart", this._showChart.bind(this));
        Solovis.UX.Utils.subscribe("@solovis/report/hide-chart", this._hideChart.bind(this));
        Solovis.UX.Utils.subscribe("@solovis/report/show-table", this.showTable.bind(this));
        Solovis.UX.Utils.subscribe("@solovis/report/hide-table", this.hideTable.bind(this));
      }
    }

    public doDestroy() {
      if (Solovis.UX.Utils.areNewControlsSupported()) {
        Solovis.UX.Utils.unsubscribe("@solovis/report/show-chart", this._showChart.bind(this));
        Solovis.UX.Utils.unsubscribe("@solovis/report/hide-chart", this._hideChart.bind(this));
        Solovis.UX.Utils.unsubscribe("@solovis/report/show-table", this.showTable.bind(this));
        Solovis.UX.Utils.unsubscribe("@solovis/report/hide-table", this.hideTable.bind(this));
      }
    }

    public _updateActionBarDiv() {
      let self = this;
      let actionBarContents = $("<div>");
      let actionBarVisible = false;

      if (this.filterManager.isFilteringEnabled()) {
        actionBarVisible = true;

        var filterIcon = $("<a><span class='fa fa-filter'></a>");
        filterIcon.appendTo(actionBarContents);

        filterIcon.click(function () {
          self._showFilteringDialog(null);
        });
      }

      this._actionBarDiv.html(actionBarContents as any);

      this._actionBarDiv.toggle(actionBarVisible);
    }

    public _expansionUpdateDataView() {
      var self = this;

      if (DataTableControl._log) {
        console.log("DataTableControl._expansionUpdateDataView");
      }

      var processedRows = this.dataView.processedRows;

      // Flatten rows.
      var flattenedRows = DataTableHelpers.TableModel.flattenVisibleRows(processedRows, {
        aggregateRowDisplay: this._aggregateRow.display,
        tableSettingsToUse: this._tableSettingsToUse,
        keepGroupSubRowsOnCollapse: this._keepGroupSubRowsOnCollapse,

        isExpandedCallback: function (nestedGroupKey) {
          return self.expansionManager.isExpanded(nestedGroupKey);
        }
      });

      this.dataView.visibleColumns = this.columnSet.getVisibleColumnDefinitions();
      this.dataView.flattenedRows = flattenedRows;
      this._postUpdateDateView(self);
    }

    public _updateDataView() {
      var self = this;

      if (DataTableControl._log) {
        console.log("DataTableControl._updateDataView");
      }

      var context = {
        dataSets: this.dataSets
      };

      var sourceDataRowObject = null;
      var rowDataObject = null;
      if (this.settings.rowData) {
        rowDataObject = Solovis.resolveObject(this.settings.rowData, context);
        if (rowDataObject && rowDataObject.length) {
          // We create a shallow copy of the array, so we can modify the row array without affecting source data.
          rowDataObject.slice(0);
        }
        else {
          // Not a data array, so just use empty array for rows.
          rowDataObject = [];
        }
        sourceDataRowObject = rowDataObject;
      }

      var processedRows = sourceDataRowObject;

      let filterFirst = this._filterFirst;
      if (filterFirst) {
        // Filter rows with filterManager settings.
        if (this.filterManager.isFilteringEnabled() || this._rowFilter) {
          processedRows = this.filterManager.filterRawRows(processedRows, this._rowFilter);
        }
      }

      // Group rows (if necessary)
      var groupResult = this.groupManager.groupAndAggregate(processedRows);

      var processedRows = groupResult.processedRows;
      var grandTotals = groupResult.grandTotals;

      if (!filterFirst) {
        // Filter rows with filterManager settings.
        if (this.filterManager.isFilteringEnabled() || this._rowFilter) {
          processedRows = this.filterManager.filterGroupedRows(processedRows, this._rowFilter);
        }
      }

      // Sort rows (if necessary)
      if (this.sortManager.isSortingEnabled()) {
        if (processedRows === rowDataObject) {
          // Copy the array since we don't want to mess with the original data set order.
          if (processedRows) {
            processedRows = processedRows.slice(0);
          }
        }

        this.sortManager.sortRows(processedRows);
      }


      // See if we need to inject sub-rows. We do this after sorting and grouping because
      //   the sub-rows are always supposed to be inserted after those steps in the order provided.
      if (this._subRowFactory) {
        if (processedRows === rowDataObject) {
          // Copy the array since we don't want to mess with the original data set.
          if (processedRows) {
            processedRows = processedRows.slice(0);
          }
        }
        processedRows = DataTableHelpers.TableModel.processSubRowFactory(this, processedRows, this._subRowFactory, this._subRowGroupKeyGetter, this._keepGroupSubRowsOnCollapse, this._removeClaimedNestedRows);
      }

      // Flatten rows.
      var flattenedRows = DataTableHelpers.TableModel.flattenVisibleRows(processedRows, {
        aggregateRowDisplay: this._aggregateRow.display,
        tableSettingsToUse: this._tableSettingsToUse,
        keepGroupSubRowsOnCollapse: this._keepGroupSubRowsOnCollapse,

        isExpandedCallback: function (nestedGroupKey) {
          return self.expansionManager.isExpanded(nestedGroupKey);
        }
      });

      var dataView = {
        rows: rowDataObject,
        processedRows: processedRows,
        flattenedRows: flattenedRows,
        grandTotals: grandTotals,
        visibleColumns: this.columnSet.getVisibleColumnDefinitions(),
        sourceDataRowCount: sourceDataRowObject ? sourceDataRowObject.length : 0
      };

      this.dataView = dataView;
      this._postUpdateDateView(self);

    }

    private _postUpdateDateView(self) {
      setTimeout(function () {
        if (self.settings.afterUpdateDataView) {
          try {
            self.settings.afterUpdateDataView.call(self);
          }
          catch (ex) {
            console.log("DataTableControl: afterUpdateDataView callback failed");
            console.log(ex);
          }
        }

        try {
          $(self).trigger("dataViewUpdated");
        }
        catch (ex) {
          console.log("DataTableControl: notifyDataViewUpdated callback failed");
          console.log(ex);
        }

        if (!self._initialPostUpdateCompleted && self._initialSavedChartData) {
          var chartDataToUse = self._initialSavedChartData;

          const enabled = Solovis.UX.Utils.areNewControlsSupported();

          if (chartDataToUse.chartState) {
            if (enabled && self._chartContainer) {
              self._showChart();
            }
            else {
              self._createChart(chartDataToUse.chartState);
            }
            if (chartDataToUse.hideTable) {
              self._showTable(false);
            }
            if (chartDataToUse.hideChart) {
              self._hideChart();
            }
          }
        }
        self._initialPostUpdateCompleted = true;
      }, 10);
    }

    public _updateColumns() {
      this.columnSet.updateColumns();
    }


    public _updateSorts() {
      this.sortManager.updateSorts();
    }

    public getDefaultSorts(sortOptions) {
      return this.sortManager.getDefaultSorts(sortOptions);
    }

    public _updateGroups() {
      this.groupManager.updateGroups();
    }


    /**
     * setRowFilter updates the data table row filter but debounces it to prevent excessive table re-rendering.
     *  rowFilter should be a function(row).
     * @param rowFilter
     */
    public setRowFilter(rowFilter) {
      var self = this;
      if (!this._debouncedUpdateRowFilter) {
        this._debouncedUpdateRowFilter = _.debounce(
          function () { self._updateRowFilter(rowFilter); },
          300);
      }

      this._debouncedUpdateRowFilter(rowFilter);
    }

    public _updateRowFilter(rowFilter) {
      this._rowFilter = rowFilter;

      this._updateDataView();
      this._renderTableData();
    }


    public _renderTableData() {
      if (!this.tableContainer)
        return;

      var noDeferredRender = Solovis.isNotNull(this.settings.noDeferredRender) ? this.settings.noDeferredRender : false;
      var noFloatingHScroll = this.settings.noFloatingHScroll ? true : false;

      if (this.reportControl.printMode) {
        // We need all rows rendered at once to be able to print, and we should turn off the floating horizontal scrollbar.
        noDeferredRender = true;
        noFloatingHScroll = true;
      }

      var tableRenderer = new DataTableHelpers.StandardTableRenderer({
        tableControl: this,
        tableContainer: this.tableContainer,
        noFloatingHScroll: noFloatingHScroll,
        noDeferredRender: noDeferredRender
      });
      this._latestTableRenderer = tableRenderer;

      tableRenderer.renderDataView(this.dataView, this._tableSettingsToUse);

      // Check table visibility based on row count.
      var showTable = true;
      if (this.dataView.sourceDataRowCount == 0) {
        if (this._hideIfNoRows) {
          showTable = false;
        }
      }

      if (showTable && this.isVisible) {
        // We also check isVisible which is a PartControl level visibility test.
        this.container.css("display", "block");
        tableRenderer.handleVisibleFixup();
      }
      else {
        this.container.css("display", "none");
      }

      this._updateActionBarDiv();
    }

    /**
     * If the data table is using deferred rendering for rows, this will ensure that all of the rows are 
     * rendered out. One use case for this is in automated tests that want to grab the table content.
     */
    public ensureAllRowsRendered() {
      if (this._latestTableRenderer && this._latestTableRenderer.ensureAllRowsRendered) {
        this._latestTableRenderer.ensureAllRowsRendered();
      }
    }

    public updateParameters(newParameters) {
      if (!(this.options as PartControlOptions).parameterOverrides) {
        this.parameters = newParameters;
      }
    }

    public updateDataSets(dataSets) {
      this.dataSets = dataSets;

      if (this._modelEnvironmentSettings) {
        if (this._modelEnvironmentSettings.path) {
          var modelEnvData = Solovis.resolveObject(this._modelEnvironmentSettings.path, this.dataSets);
          if (modelEnvData) {
            if (modelEnvData instanceof InvData.ModelEnvironment) {
              this.modelEnvironment = modelEnvData;
            }
            else {
              // We only have the json data for the environment so load it into the wrapper class.
              this.modelEnvironment = new InvData.ModelEnvironment();
              this.modelEnvironment.mergeEnvironmentData(modelEnvData);
            }
          }
        }
      }

      this._updateColumns();
      this._updateSorts();
      this._updateGroups();
      this._updateDataView();

      if (this.state >= Solovis.UI.BaseControl.ControlState.afterRender) {
        this._renderTableData();
      }
    }


    public _getCellStyle(cellValue, cellContext) {
      var cellStyle = "";

      var valueConfig = null;
      if (cellContext && cellContext.group) {
        valueConfig = cellContext.group;
      }
      else if (cellContext && cellContext.col) {
        valueConfig = cellContext.col;
      }
      else {
        valueConfig = {};
      }

      var dataType = valueConfig.dataType ? valueConfig.dataType : null;

      if (valueConfig.cellStyle != null) {
        if (typeof valueConfig.cellStyle == "string") {
          cellStyle = valueConfig.cellStyle;
        }
        else if (typeof valueConfig.cellStyle == "function") {
          cellStyle = valueConfig.cellStyle(cellValue, cellContext);
        }
      }

      return cellStyle;
    }

    public _formatCellHtml(cellValue, cellContext) {
      const cellConfig = cellContext?.group ?? cellContext?.col ?? {}

      const htmlFormatter = cellConfig.htmlFormatter ? cellConfig.htmlFormatter : null;

      if (htmlFormatter) {
        // htmlFormatter must be a function that takes (cellValue, cellContext)
        return htmlFormatter(cellValue, cellContext);
      }
      else {
        // TODO: Replace with strongly typed reference.
        return (Solovis as any).Reports.Formatter.formatValueAsHtml(cellValue, cellContext);
      }
    }


    /**
     * @param {Object} cellElement - DOM element or JQuery element to apply styles to.
     * @param {Object} col - col is the column definition to use object (NOT the Column class instance).
     */
    public _applyColumnCellStyles(cellElement: HTMLElement | JQuery, col) {
      if (cellElement instanceof jQuery) {
        cellElement = (cellElement as JQuery).get(0);
      }

      cellElement.style.textOverflow = "ellipsis";

      if (col.textAlign) {
        cellElement.style.textAlign = col.textAlign;
      }

      if (col.noWrap) {
        cellElement.style.whiteSpace = "nowrap";
      }
    }

    public _getColumnValueForRow(col: DataTableHelpers.Column | DataTableHelpers.ColumnDefinition, row) {
      var cellValue = null;
      if (col.field) {
        var colFieldType = typeof col.field;
        if (colFieldType == "string") {
          cellValue = Solovis.resolveObject(col.field, row);
        }
        else if (colFieldType == "function") {
          cellValue = col.field(row);
        }
      }

      return cellValue;
    }

    /**
     * Writes a value into a row at the path defined by col.field. Function-typed fields are not
     * writable — only string paths are supported, which matches the editable column contract.
     */
    private _setColumnValueForRow(col: DataTableHelpers.Column, row, value) {
      if (!col?.field || !row) {
        return;
      }
      if (typeof col.field === "string") {
        Solovis.setObjectValueByPath(row, col.field, value);
      }
      else {
        console.warn("DataTableControl: cannot set value on column without a string field path", col);
      }
    }

    /**
     * Called by the renderer when a user commits an edit in an editable cell. Mutates the row,
     * gives the report a chance to recompute derived cells via the onRowChanged hook, then
     * triggers a full table rebuild (which recomputes parent-group aggregates) and notifies
     * listeners via the `cellValueCommitted` event.
     */
    public onRowCellChanged(row, col: DataTableHelpers.Column, oldValue, newValue) {
      this._setColumnValueForRow(col, row, newValue);

      const onRowChanged = this._tableSettings?.onRowChanged;
      if (typeof onRowChanged === "function") {
        try {
          onRowChanged(this, row, { col: col, oldValue: oldValue, newValue: newValue });
        }
        catch (ex) {
          console.error("DataTableControl: onRowChanged callback failed", ex);
        }
      }

      this._rebuildTableAfterVeryShortDelay();

      $(this).trigger("cellValueCommitted", {
        row: row,
        col: col,
        oldValue: oldValue,
        newValue: newValue
      });
    }


    public _handleColumnsChanged(e, data) {
      if (data.modsUpdated) {
        // Mods can change aggregate and grouping options.            
        this._updateGroups();
        this._rebuildTableAfterVeryShortDelay();
      }
      else if (data.customColumnsUpdated) {
        this._updateColumnsAfterVeryShortDelay({ customColumnChanged: true });
      }
      else if (data.orderChanged || data.visibilityChanged || data.columnId) {
        this._updateColumnsAfterVeryShortDelay();
      }
    }

    // We create a debounced version of this so that many column changes can be applied successively without
    // continually updating the table.
    public _updateColumnsAfterVeryShortDelay = _.debounce(
      function (options?) {
        this.dataView.visibleColumns = this.columnSet.getVisibleColumnDefinitions();
        if (options && options.customColumnChanged) {
          this._updateSorts();
          this._updateGroups();
          this._updateDataView();
        }
        this._renderTableData();
      },
      10);

    public _handleSortByChanged(e?, data?) {
      this._rebuildTableAfterVeryShortDelay();
    }

    public _handleGroupByChanged(e?, data?) {
      this._rebuildTableAfterVeryShortDelay();
    }

    public _handleFilterByChanged(e?, data?) {
      this._rebuildTableAfterVeryShortDelay();
    }

    public _handleFilterFirstChanged(e?, data?) {
      this._rebuildTableAfterVeryShortDelay();
    }

    public _handleExpansionChanged(e?, data?) {
      this._expansionRebuildTableAfterVeryShortDelay();
    }


    public _expansionRebuildTableAfterVeryShortDelay = _.debounce(
      function () {
        // Reprocess the data and re-render the table.            
        this._expansionUpdateDataView();
        this._renderTableData();
      },
      10);

    // We create a debounced version of this so that many column changes can be applied successively without
    // continually updating the table.
    public _rebuildTableAfterVeryShortDelay = _.debounce(
      function () {
        // Reprocess the data and re-render the table.            
        this._updateDataView();
        this._renderTableData();
      },
      10);


    public _getColumnMenuLabel(col) {
      var label = col.label;
      if (col.superHeaders && col.superHeaders.length > 0) {
        for (var i = 0; i < col.superHeaders.length; i++) {
          label = col.superHeaders[i] + " : " + label;
        }
      }

      return label;
    }

    public showColumnPicker() {
      DataTableHelpers.ColumnPicker.showDialog({
        tableControl: this
      });
    }

    public showExcelExport() {
      DataTableHelpers.ExcelExport.showDialog({
        tableControl: this
      });
    }

    public showSortingAndGrouping() {
      var self = this;

      if (this._sortingAndGroupingDialog) {
        return;
      }

      var dialogDiv = $("<div>");

      var sortingAndGroupingControl = new Solovis.Reports.Parts.Tables.DataTableHelpers.SortingAndGroupingControl({
        controlHost: dialogDiv,
        tableControl: this
      });

      this._sortingAndGroupingDialog = new Solovis.UI.Dialog({
        container: dialogDiv,

        ownerDialog: Solovis.UI.Dialog.findContainingDialog(this.container),

        title: "Sorting And Grouping",
        autoOpen: true,
        width: 520,
        height: 250,
        center: true,
        actions: ["Close"],
        destroyOnClose: true,

        activate: function () {
          sortingAndGroupingControl.setAsCurrentControl();
        },

        deactivate: function () {
          sortingAndGroupingControl.controlHost.setCurrentControl(null);
          self._sortingAndGroupingDialog = null;
        }
      });
    }


    public _showFilteringDialog(column) {
      var self = this;

      if (!this._filteringDialog) {
        this._filteringDialog = new Solovis.Reports.Parts.Tables.DataTableHelpers.FilteringDialog({
          tableControl: this,
          column: column,

          close: function () {
            self._filteringDialog = null;
          }
        });
        this._filteringDialog.show();
      }
      else {
        // Dialog already open, so add filter for that column.
        this._filteringDialog.tryAddColumnFilter(column.id);
      }
    }


    public _showColumnHeaderMenu(columnId, clickEvent) {
      var self = this;

      var col = this.columnSet.getColumnById(columnId);

      var menuLinks: Solovis.UI.PopupLinkPanelLink[] = [];

      menuLinks.push({
        header: col.label
      });

      let colLinks = this._getColumnSpecificContextMenuLinks({
        col: col
      });
      menuLinks = menuLinks.concat(colLinks);

      menuLinks.push({
        header: "Table"
      });

      let tableLinks = this._getTableSpecificContextMenuLinks({
        col: col
      });
      menuLinks = menuLinks.concat(tableLinks);

      Solovis.UI.PopupLinkPanel.showLinks({
        offset: clickEvent,
        links: menuLinks,
        noPaddingAtTop: true
      });
    }

    private _getColumnSpecificContextMenuLinks(options: {
      col?: any
    }) {
      let self = this;

      let col = options.col;
      let columnId = col.id;

      let menuLinks = [];

      if (columnId.indexOf("custom_") === 0) {
        // Custom column, so allow user to edit the custom formula settings.
        var customId = columnId.substr("custom_".length);

        menuLinks.push({
          label: "Edit Custom Column",
          click: function () {
            var editCustomColumnDialog = new Solovis.Reports.Parts.Tables.DataTableHelpers.ColumnColumnDialog({
              tableControl: self,
              customId: customId
            });
            editCustomColumnDialog.show();
          }
        });
      }

      menuLinks.push({
        label: "Column Settings",
        click: function () {
          var columnSettingsDialog = new Solovis.Reports.Parts.Tables.DataTableHelpers.ColumnSettingsDialog({
            column: col
          });
          columnSettingsDialog.show();
        }
      });

      if (col.definition && col.definition.help) {
        menuLinks.push({
          label: "Column Help",
          click: function () {
            Solovis.Site.HelpManager.showCustomHelpDialog(col.definition.help, { title: col.label });
          }
        });
      }

      let moreColLinks = [];

      moreColLinks.push({
        label: "Add Custom Column",
        click: () => {
          let addCustomColumnDialog = new Solovis.Reports.Parts.Tables.DataTableHelpers.ColumnColumnDialog({
            tableControl: this,
            customId: null
          });
          addCustomColumnDialog.show();
        }
      });

      moreColLinks.push({
        label: "Hide " + col.getCompositeLabel(),
        click: () => {
          this.columnSet.toggleVisibleColumnsWithFilter(
            (changeCol, currentlyVisible) => {
              if (changeCol.id == col.id) {
                return false;
              }
              else {
                return currentlyVisible;
              }
            }, false);
        }
      });

      menuLinks.push({
        label: "More",
        links: moreColLinks
      });

      return menuLinks;
    };

    private _getTableSpecificContextMenuLinks(options: {
      col?: any
    }) {
      let self = this;

      let menuLinks = [];

      menuLinks.push({
        label: "Sorting And Grouping",
        click: function () {
          self.showSortingAndGrouping();
        }
      });
      menuLinks.push({
        label: "Filter",
        click: function () {
          self._showFilteringDialog(options.col);
        }
      });
      menuLinks.push({
        label: "Select Columns",
        click: function () {
          self.showColumnPicker();
        }
      });
      menuLinks.push({
        label: "Table Settings",
        click: function () {
          var tableSettingsDialog = new Solovis.Reports.Parts.Tables.DataTableHelpers.TableSettingsDialog({
            tableControl: self
          });
          tableSettingsDialog.show();
        }
      });

      menuLinks.push({
        label: "Quick Excel Export",
        click: function () {
          DataTableHelpers.ExcelWysiwygTableRenderer.exportTableToExcel(self);
        }
      });

      menuLinks.push({
        label: "Excel Plug-in Export",
        click: function () {
          self.showExcelExport();
        }
      });

      const enabled = Solovis.UX.Utils.areNewControlsSupported();

      let createChartLabel;

      if (enabled) {
        createChartLabel = "Create Chart";
      }
      else {
        createChartLabel = "Show As Chart";
      }

      if (!this._chartControlHost || !this._chartControlHost.currentControl) {
        // Chart is not currently shown.
        if (DataTableControl.canCurrentUserConfigureTableCharts()) {
          menuLinks.push({
            label: createChartLabel,
            click: function () {
              self._createChart(
                {
                  // Include one blank series by default.
                  seriesExportList: [{}]
                },
                true);
            }
          });
        }
      }
      else if (enabled && !this.isChartVisible()) {
        if (DataTableControl.canCurrentUserConfigureTableCharts()) {
          menuLinks.push({
            label: "Show Chart",
            click: function () {
              self._showChart();
            }
          });
        }
      }

      return menuLinks;
    }


    public static canCurrentUserConfigureTableCharts() {
      if (Solovis.Portal.CachedPermissions.doesUserHavePermission("TableChartEdit")) {
        return true;
      }
      else {
        return false;
      }
    }


    public getPersistedSettings() {
      var visibleColumnIds = this.columnSet.getVisibleColumnIds();
      var hiddenColumnIds = this.columnSet.getHiddenColumnIds();
      var columnOrderIds = this.columnSet.getColumnOrderIds();
      var columnMods = this.columnSet.getColumnMods();
      var customColumns = this.columnSet.getSerializableCustomColumnSettings();

      var sortBy = this.sortManager.getSortByList();
      var groupBy = this.groupManager.getGroupByList();
      var filterBy = this.filterManager.getFilterByList();
      var expansionSettings = this.expansionManager.getExpansionSettings();

      var tableMods = this._tableMods ? Solovis.deepCopy(this._tableMods) : null;

      var exporter = this.rowDataExporter.getPersistedSettings();

      var persistedSettings: any = {
        visibleColumnIds: visibleColumnIds,
        hiddenColumnIds: hiddenColumnIds,
        columnOrderIds: columnOrderIds,
        columnMods: columnMods,
        sortBy: sortBy,
        groupBy: groupBy,
        filterBy: filterBy,
        expansionSettings: expansionSettings,
        tableMods: tableMods
      };

      if (exporter) {
        persistedSettings.exporter = exporter;
      }

      if (customColumns) {
        persistedSettings.customColumns = customColumns;
      }

      if (this._filterFirst) {
        persistedSettings.filterFirst = true;
      }

      if (this._tableAsChartControl) {
        let chartState = this._tableAsChartControl.getChartState();
        let chart = {
          chartState: chartState,
          hideTable: this.isTableHidden(),
          hideChart: !this.isChartVisible(),
        };

        persistedSettings.chart = chart;
        this._initialSavedChartData = chart;
      }
      else if (this._initialSavedChartData) {
          persistedSettings.chart = this._initialSavedChartData;
      }
      else {
        persistedSettings.chart = {};
      }

      return persistedSettings;
    }


    public updateTableMods(newTableMods) {
      this._tableMods = newTableMods;

      this._updateTableSettingsToUse();

      if (this.state >= Solovis.UI.BaseControl.ControlState.afterRender) {
        this._rebuildTableAfterVeryShortDelay();
      }
    }

    public _updateTableSettingsToUse() {
      let tableSettingsToUse: any = {};

      if (this.settings.marginCentered) {
        tableSettingsToUse.marginCentered = this.settings.marginCentered;
      }

      if (this._aggregateRow) {
        if (this._aggregateRow.groupHeaderColspan) {
          tableSettingsToUse.groupHeaderColspan = this._aggregateRow.groupHeaderColspan;
        }
        if (this._aggregateRow.display) {
          tableSettingsToUse.aggregateRowDisplay = this._aggregateRow.display;
        }
      }

      if (this._tableMods) {
        if (Solovis.isNotNull(this._tableMods.marginCentered)) {
          tableSettingsToUse.marginCentered = this._tableMods.marginCentered;
        }

        if (this._tableMods.aggregateRowDisplay) {
          tableSettingsToUse.aggregateRowDisplay = this._tableMods.aggregateRowDisplay;
        }

        if (this._tableMods.groupHeaderColspan) {
          tableSettingsToUse.groupHeaderColspan = this._tableMods.groupHeaderColspan;
        }

        if (this._tableMods.fitToScreen) {
          tableSettingsToUse.fitToScreen = this._tableMods.fitToScreen;
        }

        if (this._tableMods.freezeFirstColumn) {
          tableSettingsToUse.freezeFirstColumn = this._tableMods.freezeFirstColumn;
        }
      }

      if (!tableSettingsToUse.aggregateRowDisplay) {
        tableSettingsToUse.aggregateRowDisplay = "top";
      }

      if (this.settings.cellContextMenuItems) {
        // (cellContext) => returns array of items for use with PopupLinkPanel.
        tableSettingsToUse.cellContextMenuItems = this.settings.cellContextMenuItems;
      }

      if (this._tableSettings.customFunctions) {
        tableSettingsToUse.customFunctions = this._tableSettings.customFunctions;
      }

      this._tableSettingsToUse = tableSettingsToUse;
    }



    public _createChart(chartState: TableAsChartState, showAddChartOptionsOnStartup?: boolean) {
      if (!this._chartContainer) {
        this._chartContainer = $("<div>").insertBefore(this.tableContainer);
        this._chartControlHost = new Solovis.UI.ControlHost({ container: this._chartContainer });
      }
      this._chartContainer.toggle(true);

      // NOTE: Consider supporting multiple charts.

      this._tableAsChartControl = new TableAsChartControl({
        tableControl: this,
        chartState: chartState,
        showAddChartOptionsOnStartup: showAddChartOptionsOnStartup
      });
      if (!showAddChartOptionsOnStartup) { // if there isn't options dialog dispatch chart created
        Solovis.UX.Utils.emit("@solovis/report/chart-created");
        Solovis.UX.Utils.emit("@solovis/report/chart-shown");
      }
      this._chartControlHost.setCurrentControl(this._tableAsChartControl);
    }

    public _removeChart() {
      if (this._chartContainer) {
        this._chartContainer.toggle(false);
        this._chartControlHost.setCurrentControl(null);
        this._tableAsChartControl = null;
        Solovis.UX.Utils.emit("@solovis/report/chart-removed");
        Solovis.UX.Utils.emit("@solovis/report/chart-hidden");

        if (this._initialSavedChartData){
          this._initialSavedChartData = null;
        }
      }
    }

    public _showChart() {
      if (this._chartContainer) {
        this._chartContainer.toggle(true);
        setTimeout(() => {
          //trigger resize for charts that were hidden by default
          window.dispatchEvent(new Event('resize'));
        }, 0)
        Solovis.UX.Utils.emit("@solovis/report/chart-shown");
      }
    }

    public _hideChart() {
      if (this._chartContainer) {
        this._chartContainer.toggle(false);
        Solovis.UX.Utils.emit("@solovis/report/chart-hidden");
      }
    }

    private showTable() {
      this._showTable(true);
    }
    private hideTable() {
      this._showTable(false);
    }

    public _showTable(visible?: boolean) {
      if (arguments.length == 0 || visible === true) {
        this.tableContainer.toggle(true);
        Solovis.UX.Utils.emit("@solovis/report/table-shown");
      }
      else if (visible === false) {
        this.tableContainer.toggle(false);
        Solovis.UX.Utils.emit("@solovis/report/table-hidden");
      }
      else {
        console.log("ERROR: DataTableControl._showTable: unknown visible value");
      }
    }

    /**
     * Determine if the table display has been hidden (such as the TableAsChartControl might do).
     */
    public isTableHidden(): boolean {
      if ($(this.tableContainer).css('display') === 'none') {
        return true;
      }

      return false;
    }

    /**
     * Determine if the table display has been hidden (such as the TableAsChartControl might do).
     */
    public isChartVisible(): boolean {
      if (this._chartContainer && $(this._chartContainer).css('display') !== 'none') {
        return true;
      }

      return false;
    }

    public filterFirst(newValue?: boolean): boolean {
      if (arguments.length === 0) {
        return !!this._filterFirst;
      }
      else {
        newValue = !!newValue;
        if (this._filterFirst !== newValue) {
          this._filterFirst = newValue;
          $(this).trigger("filterFirstChanged");
        }
      }
    }


    /**
     * Retrieves global context menu items to use for the table. Returns array
     * of link entries used by PopupLinkPanel.
     */
    public _getTableContextMenuItems(options: {
      cellContext
    }): Solovis.UI.PopupLinkPanelLink[] {
      let links: Solovis.UI.PopupLinkPanelLink[] = [];

      let col = options.cellContext.col;
      let colObj = col ? this.columnSet.getColumnById(col.id) : null;

      let row = options.cellContext.row;
      let group = options.cellContext.group;
      let nestedGroupKey;
      if (row && row._nestedGroupKey) {
        nestedGroupKey = row._nestedGroupKey;
      }

      let groupingLinks = [];

      let isGrouped = false;

      let colGroupLink = null;
      let hasColGroupId = col && col.id && !!this.groupManager.getGroupById(col.id);
      if (hasColGroupId) {
        if (!this.groupManager.isGroupedById(col.id)) {
          let colLabel = colObj.getCompositeLabel();
          colGroupLink = {
            label: "Group By " + colLabel,
            click: () => {
              this.groupManager.alsoGroupBy({ id: col.id });
            }
          };
        }
      }

      let groupByList = this.groupManager.getGroupByList();
      if (_.isEmpty(groupByList)) {
        if (colGroupLink) {
          groupingLinks.push(colGroupLink);
        }

        let hasAllGroupId = !!this.groupManager.getGroupById("all");
        if (hasAllGroupId) {
          groupingLinks.push({
            label: "Group By All",
            click: () => {
              this.groupManager.updateGroupByList([{ id: "all" }]);
            }
          });
        }
      }
      else {
        isGrouped = true;

        if (nestedGroupKey) {
          // Flattening is not allowed on rows with backingRows at this time.
          if (!row || !row._backingRow) {
            if (this.expansionManager.isFlattenedSubGroup(nestedGroupKey)) {
              groupingLinks.push({
                label: "Unflatten Group",
                click: () => {
                  this.expansionManager.unflattenSubGroup(nestedGroupKey);
                }
              });
            }
            else {
              groupingLinks.push({
                label: "Flatten Group",
                click: () => {
                  this.expansionManager.flattenSubGroup(nestedGroupKey);
                }
              });
            }
          }
        }

        if (!_.isEmpty(groupingLinks)) {
          groupingLinks.push({ separator: true });
        }

        groupingLinks.push({
          label: "Expand All",
          click: () => {
            this.expansionManager.expandAll();
          }
        });

        groupingLinks.push({
          label: "Collapse All",
          click: () => {
            this.expansionManager.collapseAll();
          }
        });

        let expandToDepthLinks = [];
        let addExpandToDepthLink = (level) => {
          expandToDepthLinks.push({
            label: level.toString(),
            click: () => {
              this.expansionManager.expandToDepth(level);
            }
          });
        };
        for (let levelCo = 1; levelCo <= 5; levelCo++) {
          addExpandToDepthLink(levelCo);
        }

        groupingLinks.push({
          label: "Expand All To Level...",
          links: expandToDepthLinks
        });

        groupingLinks.push({
          separator: true
        });

        if (colGroupLink) {
          groupingLinks.push(colGroupLink);

          groupingLinks.push({
            separator: true
          })
        }

        if (group) {
          groupingLinks.push({
            label: "Ungroup By " + group.label,
            click: () => {
              this.groupManager.ungroupById(group.id);
            }
          });
        }

        groupingLinks.push({
          label: "No Grouping",
          click: () => {
            this.groupManager.updateGroupByList([]);
          }
        });
      }

      if (!_.isEmpty(groupingLinks)) {
        links.push({
          label: "Grouping",
          links: groupingLinks
        });
      }

      if (colObj) {
        let colLinks = this._getColumnSpecificContextMenuLinks({
          col: colObj
        });

        links.push({
          label: "Column",
          links: colLinks
        });
      }

      let tableLinks = this._getTableSpecificContextMenuLinks({
        col: colObj
      });
      links.push({
        label: "Table",
        links: tableLinks
      });

      return links;
    }
  }

}


// Register global event for auto-handling tooltip on a report table.
$(document).on('mouseenter', 'table.report-data-table > tbody > tr > td',
  function () {
    var td = $(this);
    var title = td.attr('title');
    if (!title) {
      if (this.offsetWidth < this.scrollWidth) {
        td.attr('title', td.text());
      }
    }
    else {
      if (this.offsetWidth >= this.scrollWidth && title == td.text()) {
        td.removeAttr('title');
      }
    }
  });